import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import {
  User,
  Mail,
  AlertTriangle,
  Phone,
  GraduationCap,
  FileText,
  Award,
  Briefcase,
  Upload,
  ChevronDown,
  ChevronUp,
  Keyboard,
  Search,
  Check,
  X,
  Eye,
  Trash2,
  CreditCard,
  BookOpen,
  CheckCircle,
  Info,
  Loader2,
  Building2,
} from 'lucide-react';
import {
  fetchCountries,
  getCountriesSync,
  fetchSeriesBac,
  fetchProvinces,
  fetchDiplomes,
  type NomenclatureCountry,
  type NomenclatureSerieBac,
  type NomenclatureProvince,
  type NomenclatureDiplome,
} from '@/services/nomenclatureService';
import { fetchFilieres, type NomenclatureFiliere } from '@/services/filieresService';
import {
  uploadDocument as uploadDocumentToBackend,
  fetchDocuments,
  deleteDocument as deleteDocumentFromBackend,
  getDocumentPreviewUrl,
  type DocumentRecord,
  type StudentDocumentType as BackendDocumentType,
} from '@/services/documentsService';
import { handicapOptions } from '@/types/profile.types';
import {
  SituationEtudiantLabels,
  MentionDiplomeLabels,
  ResidentStatusLabels,
  FonctionnaireStatusLabels,
  MobiliteStatusLabels,
} from '@/types/etudiant.types';
import { generateAnneesUniversitaires, getAnneeUniversitaireActuelle } from '../schemas/etudiantSchema';
import { emergencyRelationSelectOptions } from '@/shared/constants/emergencyRelation';
import { toCountryCode } from '@/shared/constants/countryCode';

// ==================== CONSTANTS ====================

const ARABIC_KEYBOARD_LAYOUT = [
  ['ض', 'ص', 'ث', 'ق', 'ف', 'غ', 'ع', 'ه', 'خ', 'ح', 'ج'],
  ['ش', 'س', 'ي', 'ب', 'ل', 'ا', 'ت', 'ن', 'م', 'ك', 'ط'],
  ['ئ', 'ء', 'ؤ', 'ر', 'لا', 'ى', 'ة', 'و', 'ز', 'ظ', 'د'],
];

const COUNTRY_PHONE_CODES = [
  { code: 'MA', name: 'Maroc', dialCode: '+212', flag: '🇲🇦' },
  { code: 'DZ', name: 'Algérie', dialCode: '+213', flag: '🇩🇿' },
  { code: 'TN', name: 'Tunisie', dialCode: '+216', flag: '🇹🇳' },
  { code: 'FR', name: 'France', dialCode: '+33', flag: '🇫🇷' },
  { code: 'ES', name: 'Espagne', dialCode: '+34', flag: '🇪🇸' },
  { code: 'BE', name: 'Belgique', dialCode: '+32', flag: '🇧🇪' },
  { code: 'CA', name: 'Canada', dialCode: '+1', flag: '🇨🇦' },
  { code: 'US', name: 'États-Unis', dialCode: '+1', flag: '🇺🇸' },
  { code: 'GB', name: 'Royaume-Uni', dialCode: '+44', flag: '🇬🇧' },
  { code: 'DE', name: 'Allemagne', dialCode: '+49', flag: '🇩🇪' },
  { code: 'IT', name: 'Italie', dialCode: '+39', flag: '🇮🇹' },
  { code: 'NL', name: 'Pays-Bas', dialCode: '+31', flag: '🇳🇱' },
  { code: 'PT', name: 'Portugal', dialCode: '+351', flag: '🇵🇹' },
  { code: 'SA', name: 'Arabie Saoudite', dialCode: '+966', flag: '🇸🇦' },
  { code: 'AE', name: 'Émirats Arabes Unis', dialCode: '+971', flag: '🇦🇪' },
  { code: 'EG', name: 'Égypte', dialCode: '+20', flag: '🇪🇬' },
  { code: 'SN', name: 'Sénégal', dialCode: '+221', flag: '🇸🇳' },
  { code: 'CI', name: 'Côte d\'Ivoire', dialCode: '+225', flag: '🇨🇮' },
  { code: 'ML', name: 'Mali', dialCode: '+223', flag: '🇲🇱' },
  { code: 'MR', name: 'Mauritanie', dialCode: '+222', flag: '🇲🇷' },
];

// Fallback data in case API fails
const FALLBACK_SERIES_BAC = [
  { code: 'SM', label: 'Sciences Mathématiques A' },
  { code: 'SMB', label: 'Sciences Mathématiques B' },
  { code: 'SVT', label: 'Sciences de la Vie et de la Terre' },
  { code: 'SPC', label: 'Sciences Physiques et Chimiques' },
  { code: 'SGC', label: 'Sciences de Gestion Comptable' },
  { code: 'SE', label: 'Sciences Économiques' },
  { code: 'L', label: 'Lettres' },
  { code: 'SH', label: 'Sciences Humaines' },
  { code: 'SA', label: 'Sciences Agronomiques' },
  { code: 'STM', label: 'Sciences et Technologies Mécaniques' },
  { code: 'STE', label: 'Sciences et Technologies Électriques' },
  { code: 'AR', label: 'Arts Appliqués' },
  { code: 'AUTRE', label: 'Autre / Étranger' },
];

const FALLBACK_DIPLOMES = [
  { id: 'BAC', labelFr: 'Baccalauréat' },
  { id: 'DEUG', labelFr: 'DEUG' },
  { id: 'DUT', labelFr: 'DUT' },
  { id: 'BTS', labelFr: 'BTS' },
  { id: 'LICENCE', labelFr: 'Licence' },
  { id: 'MASTER', labelFr: 'Master' },
  { id: 'AUTRE', labelFr: 'Autre' },
];

const FALLBACK_PROVINCES = [
  'Casablanca', 'Rabat', 'Marrakech', 'Fès', 'Tanger', 'Agadir', 'Meknès',
  'Oujda', 'Kenitra', 'Tétouan', 'Safi', 'El Jadida', 'Khouribga', 'Béni Mellal',
  'Nador', 'Taza', 'Settat', 'Mohammedia', 'Ksar El Kebir', 'Larache',
  'Province étrangère',
];

// ==================== DOCUMENT TYPES ====================

export type StudentDocumentType =
  | 'CIN_FRONT'
  | 'CIN_BACK'
  | 'PASSPORT'
  | 'DIPLOME_BAC_SCAN'
  | 'RELEVES_NOTES_BAC'
  | 'DIPLOME_ACCES_SCAN'
  | 'RELEVES_NOTES_ACCES'
  | 'RELEVES_NOTES'
  | 'CV'
  | 'LETTRE_MOTIVATION';

export interface StudentUploadedDocument {
  type: StudentDocumentType;
  file?: File; // Optional - not present for backend-stored documents
  previewUrl: string;
  // Backend document info
  backendId?: string; // ID from database
  isStoredInBackend?: boolean; // True if document is saved to backend
  isUploading?: boolean; // True while upload is in progress
}

// ==================== ZOD SCHEMA ====================

const formatAnneeUniversitaire = /^\d{4}\/\d{4}$/;

// Capitalize first letter of each word (for first names)
const capitalizeFirstLetter = (value: string): string => {
  return value
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const validateAnneeUniversitaire = (value: string): boolean => {
  if (!formatAnneeUniversitaire.test(value)) return false;
  const [annee1, annee2] = value.split('/').map(Number);
  return annee2 === annee1 + 1;
};

const studentProfileSchema = z.object({
  // Section 1: Identité
  nom: z.string().min(2, 'Nom requis (min 2 caractères)'),
  prenom: z.string().min(2, 'Prénom requis (min 2 caractères)'),
  nomArabe: z.string().optional(),
  prenomArabe: z.string().optional(),
  cnie: z.string().optional(),
  passport: z.string().optional(),
  sexe: z.enum(['M', 'F']),
  dateNaissance: z.string().min(10, 'Date de naissance requise'),
  paysOrigine: z.string().min(2, 'Pays requis'),
  handicap: z.enum(['N', 'A', 'M', 'V', 'AM', 'AV', 'MV', 'T', 'XX']),

  // Section 2: Coordonnées
  email: z.string().email('Email invalide').optional().or(z.literal('')),
  phoneCountry: z.string().optional(),
  telephone: z.string().min(9, 'Numéro de téléphone requis'),
  adresse: z.string().min(5, 'Adresse requise'),

  // Section 3: Contact d'urgence
  urgenceNom: z.string().min(2, 'Nom du contact requis'),
  urgenceRelation: z.string().min(1, 'Relation requise'),
  urgencePhoneCountry: z.string().optional(),
  urgenceNumero: z.string().min(9, 'Numéro requis'),
  urgenceAdresse: z.string().min(5, 'Adresse du contact requise'),

  // Section 4: Inscription Académique
  numeroMassarCNE: z.string().min(1, 'N° Massar/CNE obligatoire'),
  diplomeInscrit: z.string().optional(), // Auto-generated from filière
  // These fields are set by admin during enrollment and are read-only for students
  filiere: z.string().optional(), // Set by admin
  niveauFormation: z.number().int().min(1).max(10).optional().default(1), // Set by admin
  situation: z.enum(['N', 'R', 'P']).optional().default('N'), // Set by admin
  premiereAnneeInscription: z.string().optional(), // Set by admin

  // Section 5: Diplôme d'Accès
  serieBaccalaureat: z.string().min(1, 'Série du bac requise'),
  anneeObtentionBac: z.string().refine(validateAnneeUniversitaire, {
    message: 'Format invalide. Utilisez AAAA/AAAA+1',
  }),
  diplomeAcces: z.string().min(1, 'Diplôme d\'accès requis'),
  specialiteDiplomeAcces: z.string().optional(), // Optional, only if not BAC
  mentionDiplomeAcces: z.enum(['p', 'ab', 'b', 'tb', 'e', 'ns']),
  lieuObtentionDiplomeAcces: z.string().min(1, 'Lieu requis'),

  // Section 6: Statut & Aides
  resident: z.enum(['O', 'N']),
  fonctionnaire: z.enum(['O', 'N']),
  pourcentageBourse: z.number().min(0).max(100),
  mobilite: z.enum(['O', 'N']),
}).superRefine((data, ctx) => {
  // Identity rule (ministry file): Moroccans give their CNIE (or passport);
  // everyone else must give a passport number (and upload the passport scan).
  if (data.paysOrigine === 'MA' || !data.paysOrigine) {
    if (!data.cnie && !data.passport) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'CNIE ou Passeport requis', path: ['cnie'] });
    }
  } else if (!data.passport) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Numéro de passeport requis pour les non-marocains (le scan du passeport est aussi obligatoire)',
      path: ['passport'],
    });
  }

  // Validation: année obtention bac <= première année inscription
  if (data.anneeObtentionBac && data.premiereAnneeInscription) {
    const anneeBac = parseInt(data.anneeObtentionBac.split('/')[0], 10);
    const anneeInscription = parseInt(data.premiereAnneeInscription.split('/')[0], 10);
    if (anneeBac > anneeInscription) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'L\'année du bac ne peut pas être après l\'inscription',
        path: ['anneeObtentionBac'],
      });
    }
  }
});

export type StudentProfileFormData = z.infer<typeof studentProfileSchema>;

// ==================== COMPONENT PROPS ====================

export interface CampusInfo {
  id: string;
  name: string;
  city: string;
}

export interface StudentProfileFormProps {
  initialData?: Partial<StudentProfileFormData>;
  initialDocuments?: StudentUploadedDocument[];
  onSubmit: (data: StudentProfileFormData, documents: StudentUploadedDocument[]) => Promise<void>;
  onSaveProgress?: (data: Partial<StudentProfileFormData>, documents: StudentUploadedDocument[]) => void;
  isLoading?: boolean;
  isLocked?: boolean;
  campus?: CampusInfo | null;
}

export interface StudentProfileFormHandle {
  getFormValues: () => Partial<StudentProfileFormData>;
  getDocuments: () => StudentUploadedDocument[];
  submitForm: () => void;
  saveProgress: () => void;
  /**
   * Run the full submission rule (every required field + every required
   * document, CV and lettre de motivation excepted). Marks the fields in
   * error and returns the French list of what is missing; empty = can submit.
   */
  validateForSubmission: () => Promise<string[]>;
}

/** Documents required before submission — same rule as the backend and the mobile app. */
export function requiredStudentDocuments(usePassport: boolean, diplomeAcces: string | undefined): { type: StudentDocumentType; label: string }[] {
  const isBac = !diplomeAcces || /baccalaur/i.test(diplomeAcces) || diplomeAcces.toUpperCase() === 'BAC';
  const list: { type: StudentDocumentType; label: string }[] = usePassport
    ? [{ type: 'PASSPORT', label: 'Scan passeport' }]
    : [
        { type: 'CIN_FRONT', label: 'Scan CIN recto' },
        { type: 'CIN_BACK', label: 'Scan CIN verso' },
      ];
  list.push(
    { type: 'DIPLOME_BAC_SCAN', label: 'Diplôme du baccalauréat (scan)' },
    { type: 'RELEVES_NOTES_BAC', label: 'Relevé de notes du baccalauréat' },
  );
  if (!isBac) {
    list.push(
      { type: 'DIPLOME_ACCES_SCAN', label: `Diplôme d'accès (${diplomeAcces})` },
      { type: 'RELEVES_NOTES_ACCES', label: `Relevé de notes du ${diplomeAcces}` },
    );
  }
  return list;
}

// ==================== SUB-COMPONENTS ====================

// Filterable Dropdown Component - Type to filter without separate search box
// Uses React Portal to render dropdown outside of overflow:hidden containers
interface FilterableDropdownOption {
  value: string;
  label: string;
  sublabel?: string;
}

const FilterableDropdown: React.FC<{
  value: string;
  onChange: (value: string) => void;
  options: FilterableDropdownOption[];
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
}> = ({ value, onChange, options, placeholder = 'Sélectionner...', disabled = false, error = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selected = options.find(o => o.value === value);

  // Filter options based on input when typing
  const filtered = isTyping && inputValue
    ? options.filter(o =>
        o.label.toLowerCase().includes(inputValue.toLowerCase()) ||
        (o.sublabel && o.sublabel.toLowerCase().includes(inputValue.toLowerCase()))
      )
    : options;

  // Update dropdown position when opening and on scroll/resize
  useEffect(() => {
    if (!isOpen || !containerRef.current) return;

    const updatePosition = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + 4,
          left: rect.left,
          width: rect.width,
        });
      }
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  // Handle click outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const isOutsideContainer = containerRef.current && !containerRef.current.contains(target);
      const isOutsideDropdown = !dropdownRef.current || !dropdownRef.current.contains(target);

      if (isOutsideContainer && isOutsideDropdown) {
        setIsOpen(false);
        setInputValue('');
        setIsTyping(false);
      }
    };

    // Small delay to avoid immediate close on open click
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 10);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    setIsTyping(true);
    if (!isOpen) setIsOpen(true);
  };

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
    setInputValue('');
    setIsTyping(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setInputValue('');
      setIsTyping(false);
    }
    if (e.key === 'Enter' && filtered.length === 1) {
      handleSelect(filtered[0].value);
    }
  };

  const displayValue = isTyping ? inputValue : (selected?.label || '');

  // Dropdown menu rendered via portal
  const dropdownMenu = isOpen ? ReactDOM.createPortal(
    <div
      ref={dropdownRef}
      className="fixed bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto"
      style={{
        top: dropdownPosition.top,
        left: dropdownPosition.left,
        width: dropdownPosition.width,
        zIndex: 9999,
      }}
    >
      {filtered.length === 0 ? (
        <div className="px-4 py-3 text-sm text-gray-500 text-center">Aucun résultat</div>
      ) : (
        filtered.map(option => (
          <button
            key={option.value}
            type="button"
            onClick={() => handleSelect(option.value)}
            className={`w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-gray-50 transition-colors ${
              value === option.value ? 'bg-primary-50' : ''
            }`}
          >
            <div className="flex-1 min-w-0">
              <div className="text-sm text-gray-900 truncate">{option.label}</div>
              {option.sublabel && (
                <div className="text-xs text-gray-500 truncate">{option.sublabel}</div>
              )}
            </div>
            {value === option.value && <Check className="w-4 h-4 text-primary-600 flex-shrink-0 ml-2" />}
          </button>
        ))
      )}
    </div>,
    document.body
  ) : null;

  return (
    <div className="relative" ref={containerRef}>
      <div
        className={`w-full flex items-center border rounded-lg transition-colors ${
          disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
        } ${error ? 'border-red-300' : 'border-gray-300'} ${isOpen ? 'ring-2 ring-primary-500 border-primary-500' : ''}`}
      >
        <input
          ref={inputRef}
          type="text"
          value={displayValue}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-full px-4 py-2 bg-transparent rounded-lg focus:outline-none ${
            disabled ? 'cursor-not-allowed text-gray-500' : 'text-gray-900'
          } ${!displayValue ? 'text-gray-500' : ''}`}
        />
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className="px-2 py-2"
        >
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
      </div>
      {dropdownMenu}
    </div>
  );
};

// Phone Country Selector - Uses portal to avoid clipping by overflow:hidden parents
const PhoneCountrySelector: React.FC<{
  value: string;
  onChange: (code: string) => void;
}> = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const selected = COUNTRY_PHONE_CODES.find(c => c.code === value) || COUNTRY_PHONE_CODES[0];
  const filtered = COUNTRY_PHONE_CODES.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.dialCode.includes(search)
  );

  // Update dropdown position when opened
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
      });
    }
  }, [isOpen]);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        dropdownRef.current && !dropdownRef.current.contains(target) &&
        buttonRef.current && !buttonRef.current.contains(target)
      ) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1 px-2 py-2 border border-gray-300 rounded-l-lg bg-gray-50 hover:bg-gray-100 min-w-[90px]"
      >
        <span className="text-lg">{selected.flag}</span>
        <span className="text-sm text-gray-600">{selected.dialCode}</span>
        <ChevronDown className="w-3 h-3 text-gray-400" />
      </button>

      {isOpen && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          className="fixed z-[9999] w-64 bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-hidden"
          style={{ top: position.top, left: position.left }}
        >
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher..."
                className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                autoFocus
              />
            </div>
          </div>
          <div className="overflow-y-auto max-h-56">
            {filtered.map(country => (
              <button
                key={country.code}
                type="button"
                onClick={() => {
                  onChange(country.code);
                  setIsOpen(false);
                  setSearch('');
                }}
                className={`w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 ${
                  value === country.code ? 'bg-primary-50' : ''
                }`}
              >
                <span className="text-lg">{country.flag}</span>
                <span className="flex-1 text-sm">{country.name}</span>
                <span className="text-xs text-gray-500">{country.dialCode}</span>
                {value === country.code && <Check className="w-4 h-4 text-primary-600" />}
              </button>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// Virtual Arabic Keyboard
const VirtualArabicKeyboard: React.FC<{
  onKeyPress: (char: string) => void;
  onBackspace: () => void;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLDivElement>;
}> = ({ onKeyPress, onBackspace, onClose, anchorRef }) => {
  const [position, setPosition] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 300 });

  useEffect(() => {
    const updatePosition = () => {
      if (anchorRef.current) {
        const rect = anchorRef.current.getBoundingClientRect();
        const keyboardHeight = 180;
        const viewportHeight = window.innerHeight;
        const showAbove = rect.bottom + keyboardHeight > viewportHeight && rect.top > keyboardHeight;

        setPosition({
          top: showAbove ? rect.top - keyboardHeight - 5 : rect.bottom + 5,
          left: rect.left,
          width: Math.max(rect.width, 280),
        });
      }
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
    };
  }, [anchorRef]);

  return ReactDOM.createPortal(
    <div
      data-arabic-keyboard
      className="fixed z-[9999] p-2 bg-white border border-gray-300 rounded-lg shadow-xl"
      style={{ top: position.top, left: position.left, width: position.width }}
    >
      <div className="flex justify-between items-center mb-2 pb-2 border-b border-gray-200">
        <span className="text-xs text-gray-500 font-medium">لوحة المفاتيح العربية</span>
        <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="space-y-1">
        {ARABIC_KEYBOARD_LAYOUT.map((row, rowIndex) => (
          <div key={rowIndex} className="flex justify-center gap-0.5">
            {row.map((char, charIndex) => (
              <button
                key={charIndex}
                type="button"
                onClick={() => onKeyPress(char)}
                className="w-7 h-8 text-sm font-medium bg-gray-100 hover:bg-primary-100 hover:text-primary-700 rounded transition-colors border border-gray-200"
              >
                {char}
              </button>
            ))}
          </div>
        ))}
        <div className="flex justify-center gap-1 mt-2">
          <button
            type="button"
            onClick={() => onKeyPress(' ')}
            className="flex-1 h-8 text-xs bg-gray-100 hover:bg-gray-200 rounded border border-gray-200"
          >
            espace
          </button>
          <button
            type="button"
            onClick={onBackspace}
            className="w-16 h-8 text-xs bg-red-50 hover:bg-red-100 text-red-600 rounded border border-red-200"
          >
            ← effacer
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Arabic Input Component
const ArabicInput: React.FC<{
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  placeholder: string;
  error?: string;
}> = ({ value, onChange, onBlur, placeholder, error }) => {
  const [showKeyboard, setShowKeyboard] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const keyboardElement = document.querySelector('[data-arabic-keyboard]');
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node) &&
        (!keyboardElement || !keyboardElement.contains(e.target as Node))
      ) {
        setShowKeyboard(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleKeyPress = (char: string) => {
    const input = inputRef.current;
    if (input) {
      const start = input.selectionStart || value.length;
      const end = input.selectionEnd || value.length;
      const newValue = value.slice(0, start) + char + value.slice(end);
      onChange(newValue);
      setTimeout(() => {
        const newPos = start + char.length;
        input.setSelectionRange(newPos, newPos);
        input.focus();
      }, 0);
    } else {
      onChange(value + char);
    }
  };

  const handleBackspace = () => {
    const input = inputRef.current;
    if (input && value.length > 0) {
      const start = input.selectionStart || value.length;
      const end = input.selectionEnd || value.length;
      if (start === end && start > 0) {
        const newValue = value.slice(0, start - 1) + value.slice(end);
        onChange(newValue);
        setTimeout(() => {
          input.setSelectionRange(start - 1, start - 1);
          input.focus();
        }, 0);
      } else if (start !== end) {
        const newValue = value.slice(0, start) + value.slice(end);
        onChange(newValue);
        setTimeout(() => {
          input.setSelectionRange(start, start);
          input.focus();
        }, 0);
      }
    } else if (value.length > 0) {
      onChange(value.slice(0, -1));
    }
  };

  return (
    <div className="relative" ref={containerRef}>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => setTimeout(onBlur, 100)}
        dir="rtl"
        className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-right ${
          error ? 'border-red-300' : 'border-gray-300'
        }`}
        placeholder={placeholder}
      />
      <button
        type="button"
        onClick={() => {
          setShowKeyboard(!showKeyboard);
          if (!showKeyboard) inputRef.current?.focus();
        }}
        className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded transition-colors ${
          showKeyboard ? 'bg-primary-100 text-primary-600' : 'text-gray-400 hover:text-gray-600'
        }`}
        title={showKeyboard ? 'Fermer le clavier' : 'Ouvrir le clavier arabe'}
      >
        <Keyboard className="w-4 h-4" />
      </button>

      {showKeyboard && (
        <VirtualArabicKeyboard
          onKeyPress={handleKeyPress}
          onBackspace={handleBackspace}
          onClose={() => setShowKeyboard(false)}
          anchorRef={containerRef}
        />
      )}
    </div>
  );
};

// Document Row Component
const DocumentRow: React.FC<{
  label: string;
  required: boolean;
  document?: StudentUploadedDocument;
  onUpload: (file: File) => void;
  onRemove: () => void;
  onPreview: () => void;
  disabled?: boolean;
}> = ({ label, required, document, onUpload, onRemove, onPreview, disabled = false }) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onUpload(file);
    if (inputRef.current) inputRef.current.value = '';
  };

  const isUploading = document?.isUploading;
  const isStored = document?.isStoredInBackend;

  return (
    <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0">
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-700">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </span>
      </div>

      <div className="flex items-center gap-2">
        {isUploading ? (
          <div className="flex items-center gap-2 px-3 py-1.5 text-sm text-amber-600 bg-amber-50 rounded">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>Envoi en cours...</span>
          </div>
        ) : document ? (
          <>
            <span className={`text-xs px-2 py-1 rounded ${isStored ? 'text-green-600 bg-green-50' : 'text-amber-600 bg-amber-50'}`}>
              {isStored ? 'Enregistré' : 'En attente'}
            </span>
            <button type="button" onClick={onPreview} className="p-1.5 text-gray-500 hover:text-primary-600 hover:bg-gray-100 rounded" title="Voir">
              <Eye className="w-4 h-4" />
            </button>
            <button type="button" onClick={onRemove} disabled={disabled} className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded disabled:opacity-50 disabled:cursor-not-allowed" title="Supprimer">
              <Trash2 className="w-4 h-4" />
            </button>
          </>
        ) : (
          <label className={`flex items-center gap-2 px-3 py-1.5 text-sm text-primary-600 bg-primary-50 hover:bg-primary-100 rounded transition-colors ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
            <Upload className="w-4 h-4" />
            <span>Télécharger</span>
            <input ref={inputRef} type="file" accept="image/*,.pdf" onChange={handleFileChange} className="hidden" disabled={disabled} />
          </label>
        )}
      </div>
    </div>
  );
};

// Section Header Component
const SectionHeader: React.FC<{
  title: string;
  icon: React.ReactNode;
  expanded: boolean;
  onToggle: () => void;
  complete?: boolean;
}> = ({ title, icon, expanded, onToggle, complete }) => (
  <button type="button" onClick={onToggle} className="w-full flex items-center justify-between py-4 px-4 text-left bg-gray-50 border-b border-gray-200">
    <div className="flex items-center gap-3">
      <div className="p-2 bg-primary-100 rounded-lg">{icon}</div>
      <span className="font-semibold text-gray-900">{title}</span>
      {complete && <CheckCircle className="w-5 h-5 text-green-500" />}
    </div>
    {expanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
  </button>
);

// ==================== MAIN COMPONENT ====================

export const StudentProfileForm = forwardRef<StudentProfileFormHandle, StudentProfileFormProps>(
  ({ initialData, initialDocuments = [], onSubmit, onSaveProgress, isLocked = false, campus }, ref) => {
    const [expandedSections, setExpandedSections] = useState({
      identity: true,
      coordinates: false,
      emergency: false,
      academic: false,
      diploma: false,
      status: false,
      documents: false,
    });

    // Nomenclature states
    const [countries, setCountries] = useState<NomenclatureCountry[]>(() => getCountriesSync());
    const [filieres, setFilieres] = useState<NomenclatureFiliere[]>([]);
    const [, setFilieresLoaded] = useState(false);
    const [seriesBac, setSeriesBac] = useState<NomenclatureSerieBac[]>([]);
    const [provinces, setProvinces] = useState<NomenclatureProvince[]>([]);
    const [diplomes, setDiplomes] = useState<NomenclatureDiplome[]>([]);

    // Selected filière for dynamic niveau (setter used in useEffect to track filiere selection)
    const [, setSelectedFiliere] = useState<NomenclatureFiliere | null>(null);

    // Documents
    const [documents, setDocuments] = useState<StudentUploadedDocument[]>(initialDocuments || []);
    const [previewDocument, setPreviewDocument] = useState<StudentUploadedDocument | null>(null);
    const [idDocumentType, setIdDocumentType] = useState<'CIN' | 'PASSPORT'>('CIN');
    const [documentsLoading, setDocumentsLoading] = useState(false);

    const anneesUniversitaires = generateAnneesUniversitaires(20, true);

    const {
      register,
      control,
      handleSubmit,
      watch,
      setValue,
      getValues,
      reset,
      formState: { errors },
    } = useForm<StudentProfileFormData>({
      resolver: zodResolver(studentProfileSchema),
      defaultValues: {
        nom: initialData?.nom || '',
        prenom: initialData?.prenom || '',
        nomArabe: initialData?.nomArabe || '',
        prenomArabe: initialData?.prenomArabe || '',
        cnie: initialData?.cnie || '',
        passport: initialData?.passport || '',
        sexe: initialData?.sexe || 'M',
        dateNaissance: initialData?.dateNaissance || '',
        paysOrigine: toCountryCode(initialData?.paysOrigine) || 'MA',
        handicap: initialData?.handicap || 'N',
        email: initialData?.email || '',
        phoneCountry: 'MA',
        telephone: initialData?.telephone || '',
        adresse: initialData?.adresse || '',
        urgenceNom: initialData?.urgenceNom || '',
        urgenceRelation: initialData?.urgenceRelation || '',
        urgencePhoneCountry: 'MA',
        urgenceNumero: initialData?.urgenceNumero || '',
        urgenceAdresse: initialData?.urgenceAdresse || '',
        numeroMassarCNE: initialData?.numeroMassarCNE || '',
        diplomeInscrit: initialData?.diplomeInscrit || '',
        filiere: initialData?.filiere || '',
        niveauFormation: initialData?.niveauFormation || 1,
        situation: initialData?.situation || 'N',
        premiereAnneeInscription: initialData?.premiereAnneeInscription || getAnneeUniversitaireActuelle(),
        serieBaccalaureat: initialData?.serieBaccalaureat || '',
        anneeObtentionBac: initialData?.anneeObtentionBac || '',
        diplomeAcces: initialData?.diplomeAcces || 'Baccalauréat',
        specialiteDiplomeAcces: initialData?.specialiteDiplomeAcces || '',
        mentionDiplomeAcces: initialData?.mentionDiplomeAcces || 'ns',
        lieuObtentionDiplomeAcces: initialData?.lieuObtentionDiplomeAcces || '',
        resident: initialData?.resident || 'N',
        fonctionnaire: initialData?.fonctionnaire || 'N',
        pourcentageBourse: initialData?.pourcentageBourse || 0,
        mobilite: initialData?.mobilite || 'N',
      },
    });

    // Track the last data hash we've seen to detect real changes from storage
    const lastDataHashRef = useRef<string>('');

    // Reset form when initialData changes with new meaningful data from storage
    useEffect(() => {
      if (initialData && Object.keys(initialData).length > 0) {
        // Create a comprehensive hash of the data that matters
        const dataHash = JSON.stringify({
          nom: initialData.nom || '',
          prenom: initialData.prenom || '',
          nomArabe: initialData.nomArabe || '',
          prenomArabe: initialData.prenomArabe || '',
          cnie: initialData.cnie || '',
          passport: initialData.passport || '',
          sexe: initialData.sexe || '',
          dateNaissance: initialData.dateNaissance || '',
          paysOrigine: toCountryCode(initialData.paysOrigine),
          handicap: initialData.handicap || '',
          email: initialData.email || '',
          telephone: initialData.telephone || '',
          adresse: initialData.adresse || '',
          urgenceNom: initialData.urgenceNom || '',
          urgenceRelation: initialData.urgenceRelation || '',
          urgenceNumero: initialData.urgenceNumero || '',
          urgenceAdresse: initialData.urgenceAdresse || '',
          numeroMassarCNE: initialData.numeroMassarCNE || '',
          diplomeInscrit: initialData.diplomeInscrit || '',
          filiere: initialData.filiere || '',
          niveauFormation: initialData.niveauFormation || 1,
          situation: initialData.situation || '',
          premiereAnneeInscription: initialData.premiereAnneeInscription || '',
          serieBaccalaureat: initialData.serieBaccalaureat || '',
          anneeObtentionBac: initialData.anneeObtentionBac || '',
          diplomeAcces: initialData.diplomeAcces || '',
          specialiteDiplomeAcces: initialData.specialiteDiplomeAcces || '',
          mentionDiplomeAcces: initialData.mentionDiplomeAcces || '',
          lieuObtentionDiplomeAcces: initialData.lieuObtentionDiplomeAcces || '',
          resident: initialData.resident || '',
          fonctionnaire: initialData.fonctionnaire || '',
          pourcentageBourse: initialData.pourcentageBourse || 0,
          mobilite: initialData.mobilite || '',
        });

        // Only reset if this is genuinely new/different data
        if (dataHash !== lastDataHashRef.current) {
          console.log('Resetting form with initialData:', initialData);
          reset({
            nom: initialData.nom || '',
            prenom: initialData.prenom || '',
            nomArabe: initialData.nomArabe || '',
            prenomArabe: initialData.prenomArabe || '',
            cnie: initialData.cnie || '',
            passport: initialData.passport || '',
            sexe: initialData.sexe || 'M',
            dateNaissance: initialData.dateNaissance || '',
            paysOrigine: toCountryCode(initialData.paysOrigine) || 'MA',
            handicap: initialData.handicap || 'N',
            email: initialData.email || '',
            phoneCountry: 'MA',
            telephone: initialData.telephone || '',
            adresse: initialData.adresse || '',
            urgenceNom: initialData.urgenceNom || '',
            urgenceRelation: initialData.urgenceRelation || '',
            urgencePhoneCountry: 'MA',
            urgenceNumero: initialData.urgenceNumero || '',
            urgenceAdresse: initialData.urgenceAdresse || '',
            numeroMassarCNE: initialData.numeroMassarCNE || '',
            diplomeInscrit: initialData.diplomeInscrit || '',
            filiere: initialData.filiere || '',
            niveauFormation: initialData.niveauFormation || 1,
            situation: initialData.situation || 'N',
            premiereAnneeInscription: initialData.premiereAnneeInscription || getAnneeUniversitaireActuelle(),
            serieBaccalaureat: initialData.serieBaccalaureat || '',
            anneeObtentionBac: initialData.anneeObtentionBac || '',
            diplomeAcces: initialData.diplomeAcces || 'Baccalauréat',
            specialiteDiplomeAcces: initialData.specialiteDiplomeAcces || '',
            mentionDiplomeAcces: initialData.mentionDiplomeAcces || 'ns',
            lieuObtentionDiplomeAcces: initialData.lieuObtentionDiplomeAcces || '',
            resident: initialData.resident || 'N',
            fonctionnaire: initialData.fonctionnaire || 'N',
            pourcentageBourse: initialData.pourcentageBourse || 0,
            mobilite: initialData.mobilite || 'N',
          });
          lastDataHashRef.current = dataHash;
        }
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialData]);

    const watchedFiliere = watch('filiere');
    const watchedDiplomeAcces = watch('diplomeAcces');

    // Load all nomenclatures
    useEffect(() => {
      const loadData = async () => {
        try {
          // Countries
          const countriesData = await fetchCountries();
          if (countriesData?.length) setCountries(countriesData);

          // Filières from institution
          const filieresData = await fetchFilieres(true, false);
          setFilieres(filieresData.filter(f => f.isActive));
          setFilieresLoaded(true);

          // Séries Bac
          const seriesBacData = await fetchSeriesBac();
          if (seriesBacData?.length) {
            setSeriesBac(seriesBacData);
          }

          // Provinces
          const provincesData = await fetchProvinces();
          if (provincesData?.length) {
            setProvinces(provincesData);
          }

          // Diplômes
          const diplomesData = await fetchDiplomes();
          if (diplomesData?.length) {
            setDiplomes(diplomesData);
          }
        } catch (error) {
          console.error('Error loading nomenclatures:', error);
          // Mark filières as loaded even on error to show proper message
          setFilieresLoaded(true);
        }
      };
      loadData();
    }, []);

    // Auto-fill diplome when filiere changes
    useEffect(() => {
      if (watchedFiliere && filieres.length > 0) {
        const selected = filieres.find(f => f.filiere === watchedFiliere || f.id === watchedFiliere);
        if (selected) {
          setSelectedFiliere(selected);
          setValue('diplomeInscrit', selected.diplome);
          // Reset niveau if it exceeds program years
          const currentNiveau = getValues('niveauFormation');
          if (currentNiveau > selected.nombreAnnees) {
            setValue('niveauFormation', 1);
          }
        }
      }
    }, [watchedFiliere, filieres, setValue, getValues]);

    // Toggle section
    const toggleSection = (section: keyof typeof expandedSections) => {
      setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
    };

    // Load documents from backend on mount
    useEffect(() => {
      const loadDocumentsFromBackend = async () => {
        try {
          setDocumentsLoading(true);
          const backendDocs = await fetchDocuments();
          if (backendDocs && backendDocs.length > 0) {
            const loadedDocs: StudentUploadedDocument[] = backendDocs.map((doc: DocumentRecord) => ({
              type: doc.documentType as StudentDocumentType,
              previewUrl: getDocumentPreviewUrl(doc.fileUrl),
              backendId: doc.id,
              isStoredInBackend: true,
            }));
            setDocuments(loadedDocs);

            // Set idDocumentType based on loaded documents
            const hasCIN = loadedDocs.some(d => d.type === 'CIN_FRONT' || d.type === 'CIN_BACK');
            const hasPassport = loadedDocs.some(d => d.type === 'PASSPORT');
            if (hasPassport && !hasCIN) {
              setIdDocumentType('PASSPORT');
            } else {
              setIdDocumentType('CIN');
            }
          }
        } catch (error) {
          console.error('Error loading documents from backend:', error);
        } finally {
          setDocumentsLoading(false);
        }
      };
      loadDocumentsFromBackend();
    }, []);

    // Document handling - Upload to backend immediately
    const handleUpload = useCallback(async (type: StudentDocumentType, file: File) => {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Le fichier est trop grand (max 10MB)');
        return;
      }

      // Create a local preview URL
      const previewUrl = URL.createObjectURL(file);

      // Add document to state with uploading status
      setDocuments(prev => {
        const filtered = prev.filter(d => d.type !== type);
        return [...filtered, { type, file, previewUrl, isUploading: true }];
      });

      try {
        // Upload to backend
        const result = await uploadDocumentToBackend(type as BackendDocumentType, file);

        // Update document state with backend info
        setDocuments(prev => prev.map(d =>
          d.type === type
            ? {
                ...d,
                backendId: result.id,
                isStoredInBackend: true,
                isUploading: false,
                previewUrl: getDocumentPreviewUrl(result.fileUrl),
              }
            : d
        ));

        // Revoke the local blob URL since we now have the backend URL
        URL.revokeObjectURL(previewUrl);
      } catch (error) {
        console.error('Error uploading document:', error);
        // Provide more helpful error messages
        let errorMessage = 'Erreur inconnue';
        if (error instanceof Error) {
          if (error.message === 'Failed to fetch') {
            errorMessage = 'Erreur de connexion au serveur. Veuillez vérifier votre connexion internet et réessayer.';
          } else {
            errorMessage = error.message;
          }
        }
        toast.error(`Erreur lors de l'upload du document: ${errorMessage}`);

        // Remove failed upload from state
        setDocuments(prev => {
          URL.revokeObjectURL(previewUrl);
          return prev.filter(d => d.type !== type);
        });
      }
    }, []);

    const handleRemove = useCallback(async (type: StudentDocumentType) => {
      const doc = documents.find(d => d.type === type);
      if (!doc) return;

      // If stored in backend, delete from backend first
      if (doc.isStoredInBackend && doc.backendId) {
        try {
          await deleteDocumentFromBackend(doc.backendId);
        } catch (error) {
          console.error('Error deleting document from backend:', error);
          toast.error(`Erreur lors de la suppression: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
          return;
        }
      }

      // Remove from local state
      setDocuments(prev => {
        const docToRemove = prev.find(d => d.type === type);
        if (docToRemove && docToRemove.previewUrl.startsWith('blob:')) {
          URL.revokeObjectURL(docToRemove.previewUrl);
        }
        return prev.filter(d => d.type !== type);
      });
    }, [documents]);

    const getDocument = (type: StudentDocumentType) => documents.find(d => d.type === type);

    // Form submission
    const onFormSubmit = async (data: StudentProfileFormData) => {
      await onSubmit(data, documents);
    };

    // Save progress handler
    const handleSaveProgress = useCallback(() => {
      if (onSaveProgress) {
        const data = getValues();
        onSaveProgress(data, documents);
      }
    }, [getValues, documents, onSaveProgress]);

    // Full submission rule: schema (required fields, CNIE or passport, formats)
    // + required documents. Field errors stay visible in the form; the list is
    // shown by the caller as one banner.
    const validateForSubmission = useCallback(async (): Promise<string[]> => {
      const messages: string[] = [];
      const collect = (node: unknown): void => {
        if (!node || typeof node !== 'object') return;
        const maybe = node as { message?: unknown };
        if (typeof maybe.message === 'string') {
          messages.push(maybe.message);
          return;
        }
        Object.values(node as Record<string, unknown>).forEach(collect);
      };
      // handleSubmit hands the fresh error tree to onInvalid (formState.errors
      // captured by this closure could be stale) and marks the fields in red.
      await new Promise<void>((resolve) => {
        void handleSubmit(
          () => resolve(),
          (invalid) => {
            collect(invalid);
            resolve();
          },
        )();
      });
      const values = getValues();
      for (const doc of requiredStudentDocuments(idDocumentType === 'PASSPORT', values.diplomeAcces)) {
        if (!documents.some((d) => d.type === doc.type)) {
          messages.push(`Document manquant : ${doc.label}`);
        }
      }
      return Array.from(new Set(messages));
    }, [handleSubmit, getValues, documents, idDocumentType]);

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
      getFormValues: () => getValues(),
      getDocuments: () => documents,
      submitForm: () => handleSubmit(onFormSubmit)(),
      saveProgress: handleSaveProgress,
      validateForSubmission,
    }));

    // Check if diplome acces is BAC (default)
    const isBacDiplome = !watchedDiplomeAcces || watchedDiplomeAcces === 'Baccalauréat' || watchedDiplomeAcces === 'BAC';
    const requiresDiplomeAccesScan = !isBacDiplome;

    // Get series bac list (from API or fallback) - grouped by type (Marocain/Étranger)
    const seriesBacList = seriesBac.length > 0
      ? seriesBac.map(s => ({
          code: s.code,
          label: s.labelFr,
          type: s.type || 'Marocain' // Default to Marocain if type is not set
        }))
      : FALLBACK_SERIES_BAC.map(s => ({ ...s, type: 'Marocain' }));

    // Get diplomes list (from API or fallback)
    const diplomesList = diplomes.length > 0
      ? diplomes.map(d => ({ id: d.id, label: d.labelFr }))
      : FALLBACK_DIPLOMES.map(d => ({ id: d.id, label: d.labelFr }));

    // Get provinces list (from API or fallback)
    const provincesList = provinces.length > 0
      ? provinces.map(p => p.nameFr)
      : FALLBACK_PROVINCES;

    return (
      <div className="space-y-4">
        <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-4">
          {/* Info Banner */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-700">
              <p className="font-medium mb-1">Profil Étudiant E-DEP</p>
              <p>Remplissez toutes les sections pour compléter votre inscription. Les champs marqués d'un * sont obligatoires.</p>
            </div>
          </div>

          {/* Section 1: Identité */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <SectionHeader
              title="Section 1: Identité"
              icon={<User className="w-5 h-5 text-primary-600" />}
              expanded={expandedSections.identity}
              onToggle={() => toggleSection('identity')}
            />
            {expandedSections.identity && (
              <div className="p-6 space-y-4">
                {/* Nom / Prénom */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nom <span className="text-red-500">*</span></label>
                    <input {...register('nom')} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 uppercase" placeholder="NOM" disabled={isLocked} />
                    {errors.nom && <p className="text-xs text-red-500 mt-1">{errors.nom.message}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Prénom <span className="text-red-500">*</span></label>
                    <input
                      {...register('prenom', {
                        onChange: (e) => {
                          e.target.value = capitalizeFirstLetter(e.target.value);
                        }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="Prénom"
                      disabled={isLocked}
                    />
                    {errors.prenom && <p className="text-xs text-red-500 mt-1">{errors.prenom.message}</p>}
                  </div>
                </div>

                {/* Nom / Prénom Arabe */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">الاسم العائلي / Nom en arabe <span className="text-gray-400 text-xs">(optionnel)</span></label>
                    <Controller name="nomArabe" control={control} render={({ field }) => (
                      <ArabicInput value={field.value || ''} onChange={field.onChange} onBlur={field.onBlur} placeholder="الاسم العائلي" error={errors.nomArabe?.message} />
                    )} />
                    {errors.nomArabe && <p className="text-xs text-red-500 mt-1">{errors.nomArabe.message}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">الاسم الشخصي / Prénom en arabe <span className="text-gray-400 text-xs">(optionnel)</span></label>
                    <Controller name="prenomArabe" control={control} render={({ field }) => (
                      <ArabicInput value={field.value || ''} onChange={field.onChange} onBlur={field.onBlur} placeholder="الاسم الشخصي" error={errors.prenomArabe?.message} />
                    )} />
                    {errors.prenomArabe && <p className="text-xs text-red-500 mt-1">{errors.prenomArabe.message}</p>}
                  </div>
                </div>

                {/* CIN ou Passeport - At least one required */}
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-2">
                  <p className="text-sm text-amber-800 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    <span><strong>Document d'identité requis:</strong> Remplissez soit la CNIE (marocains) soit le Passeport (étrangers). Au moins un des deux est obligatoire.</span>
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      N° CNIE <span className="text-gray-500 text-xs">(marocains)</span>
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <input
                      {...register('cnie')}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${errors.cnie ? 'border-red-300' : 'border-gray-300'}`}
                      placeholder="AB123456"
                      disabled={isLocked}
                    />
                    {errors.cnie && <p className="text-xs text-red-500 mt-1">{errors.cnie.message}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      N° Passeport <span className="text-gray-500 text-xs">(étrangers)</span>
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <input
                      {...register('passport')}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${errors.passport ? 'border-red-300' : 'border-gray-300'}`}
                      placeholder="Numéro passeport"
                      disabled={isLocked}
                    />
                    {errors.passport && <p className="text-xs text-red-500 mt-1">{errors.passport.message}</p>}
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">* Un seul document requis - ne remplissez qu'un seul des deux champs.</p>

                {/* Sexe / Date de naissance */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Sexe <span className="text-red-500">*</span></label>
                    <Controller name="sexe" control={control} render={({ field }) => (
                      <div className="flex gap-4">
                        <label className={`flex-1 flex items-center justify-center gap-2 p-3 border rounded-lg cursor-pointer transition-colors ${field.value === 'M' ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-300 hover:border-gray-400'}`}>
                          <input type="radio" value="M" checked={field.value === 'M'} onChange={() => field.onChange('M')} className="sr-only" disabled={isLocked} />
                          <span>Masculin</span>
                        </label>
                        <label className={`flex-1 flex items-center justify-center gap-2 p-3 border rounded-lg cursor-pointer transition-colors ${field.value === 'F' ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-300 hover:border-gray-400'}`}>
                          <input type="radio" value="F" checked={field.value === 'F'} onChange={() => field.onChange('F')} className="sr-only" disabled={isLocked} />
                          <span>Féminin</span>
                        </label>
                      </div>
                    )} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date de naissance <span className="text-red-500">*</span></label>
                    <input
                      type="date"
                      {...register('dateNaissance')}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 appearance-none ${errors.dateNaissance ? 'border-red-300' : 'border-gray-300'}`}
                      disabled={isLocked}
                      style={{ colorScheme: 'light' }}
                    />
                    {errors.dateNaissance && <p className="text-xs text-red-500 mt-1">{errors.dateNaissance.message}</p>}
                  </div>
                </div>

                {/* Pays / Handicap */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Pays d'origine <span className="text-red-500">*</span></label>
                    <select {...register('paysOrigine')} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" disabled={isLocked}>
                      {countries.map(c => <option key={c.code} value={c.code}>{c.nameFr}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Situation de handicap <span className="text-red-500">*</span></label>
                    <select {...register('handicap')} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" disabled={isLocked}>
                      {handicapOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Section 2: Coordonnées */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <SectionHeader title="Section 2: Coordonnées" icon={<Phone className="w-5 h-5 text-primary-600" />} expanded={expandedSections.coordinates} onToggle={() => toggleSection('coordinates')} />
            {expandedSections.coordinates && (
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email institutionnel <span className="text-gray-400 font-normal">(optionnel)</span></label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input {...register('email')} type="email" className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" placeholder="email@institution.ma" disabled={isLocked} />
                  </div>
                  {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Numéro de téléphone <span className="text-red-500">*</span></label>
                  <div className="flex">
                    <Controller name="phoneCountry" control={control} render={({ field }) => (
                      <PhoneCountrySelector value={field.value || 'MA'} onChange={field.onChange} />
                    )} />
                    <input {...register('telephone')} type="tel" className="flex-1 px-4 py-2 border border-l-0 border-gray-300 rounded-r-lg focus:ring-2 focus:ring-primary-500" placeholder="6XX XXX XXX" disabled={isLocked} />
                  </div>
                  {errors.telephone && <p className="text-xs text-red-500 mt-1">{errors.telephone.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Adresse <span className="text-red-500">*</span></label>
                  <textarea {...register('adresse')} rows={2} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" placeholder="Adresse complète" disabled={isLocked} />
                  {errors.adresse && <p className="text-xs text-red-500 mt-1">{errors.adresse.message}</p>}
                </div>
              </div>
            )}
          </div>

          {/* Section 3: Contact d'Urgence */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <SectionHeader title="Section 3: Contact d'Urgence" icon={<AlertTriangle className="w-5 h-5 text-amber-500" />} expanded={expandedSections.emergency} onToggle={() => toggleSection('emergency')} />
            {expandedSections.emergency && (
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nom du contact <span className="text-red-500">*</span></label>
                    <input
                      {...register('urgenceNom', {
                        onChange: (e) => {
                          e.target.value = capitalizeFirstLetter(e.target.value);
                        }
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="Nom complet"
                      disabled={isLocked}
                    />
                    {errors.urgenceNom && <p className="text-xs text-red-500 mt-1">{errors.urgenceNom.message}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Relation <span className="text-red-500">*</span></label>
                    <select {...register('urgenceRelation')} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" disabled={isLocked}>
                      {emergencyRelationSelectOptions().map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </select>
                    {errors.urgenceRelation && <p className="text-xs text-red-500 mt-1">{errors.urgenceRelation.message}</p>}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Numéro de téléphone <span className="text-red-500">*</span></label>
                  <div className="flex">
                    <Controller name="urgencePhoneCountry" control={control} render={({ field }) => (
                      <PhoneCountrySelector value={field.value || 'MA'} onChange={field.onChange} />
                    )} />
                    <input {...register('urgenceNumero')} type="tel" className="flex-1 px-4 py-2 border border-l-0 border-gray-300 rounded-r-lg focus:ring-2 focus:ring-primary-500" placeholder="6XX XXX XXX" disabled={isLocked} />
                  </div>
                  {errors.urgenceNumero && <p className="text-xs text-red-500 mt-1">{errors.urgenceNumero.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Adresse du contact <span className="text-red-500">*</span></label>
                  <input {...register('urgenceAdresse')} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" placeholder="Adresse complète" disabled={isLocked} />
                  {errors.urgenceAdresse && <p className="text-xs text-red-500 mt-1">{errors.urgenceAdresse.message}</p>}
                </div>
              </div>
            )}
          </div>

          {/* Section 4: Inscription Académique */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <SectionHeader title="Section 4: Inscription Académique" icon={<GraduationCap className="w-5 h-5 text-primary-600" />} expanded={expandedSections.academic} onToggle={() => toggleSection('academic')} />
            {expandedSections.academic && (
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">N° Massar/CNE <span className="text-red-500">*</span></label>
                    <input
                      {...register('numeroMassarCNE')}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${errors.numeroMassarCNE ? 'border-red-300' : 'border-gray-300'}`}
                      placeholder="R123456789 ou 'be'"
                      disabled={isLocked}
                    />
                    <p className="text-xs text-gray-500 mt-1">'be' pour les étudiants ayant obtenu un baccalauréat non marocain</p>
                    {errors.numeroMassarCNE && <p className="text-xs text-red-500 mt-1">{errors.numeroMassarCNE.message}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Filière</label>
                    <input
                      {...register('filiere')}
                      readOnly
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                      placeholder="Définie par l'administration"
                    />
                    <p className="text-xs text-gray-500 mt-1">Ce champ est défini lors de votre inscription</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Diplôme d'inscription</label>
                    <input {...register('diplomeInscrit')} readOnly className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600" placeholder="Auto-rempli selon la filière" />
                    <p className="text-xs text-gray-500 mt-1">Généré automatiquement selon la filière</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Niveau d'inscription</label>
                    <Controller name="niveauFormation" control={control} render={({ field }) => (
                      <input
                        type="text"
                        value={field.value === 1 ? '1ère année' : `${field.value}ème année`}
                        readOnly
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                      />
                    )} />
                    <p className="text-xs text-gray-500 mt-1">Ce champ est défini lors de votre inscription</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Situation</label>
                    <Controller name="situation" control={control} render={({ field }) => (
                      <input
                        type="text"
                        value={SituationEtudiantLabels[field.value as keyof typeof SituationEtudiantLabels] || field.value || 'Non définie'}
                        readOnly
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                      />
                    )} />
                    <p className="text-xs text-gray-500 mt-1">Ce champ est défini lors de votre inscription</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">1ère année d'inscription</label>
                    <input
                      {...register('premiereAnneeInscription')}
                      readOnly
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                      placeholder="Non définie"
                    />
                    <p className="text-xs text-gray-500 mt-1">Ce champ est défini lors de votre inscription</p>
                  </div>
                </div>

                {/* Campus d'affectation - Read-only display */}
                {campus && (
                  <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-blue-600" />
                        Campus d'affectation
                      </div>
                    </label>
                    <div className="text-gray-900 font-medium">{campus.name}</div>
                    <div className="text-sm text-gray-500">{campus.city}</div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Section 5: Diplôme d'Accès */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <SectionHeader title="Section 5: Diplôme d'Accès" icon={<Award className="w-5 h-5 text-primary-600" />} expanded={expandedSections.diploma} onToggle={() => toggleSection('diploma')} />
            {expandedSections.diploma && (
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Série du baccalauréat <span className="text-red-500">*</span></label>
                    <Controller
                      name="serieBaccalaureat"
                      control={control}
                      render={({ field }) => (
                        <FilterableDropdown
                          value={field.value}
                          onChange={field.onChange}
                          options={seriesBacList.map(s => {
                            const isEtranger = s.type?.toLowerCase().includes('etranger') || s.type?.toLowerCase().includes('étranger');
                            return {
                              value: s.code,
                              label: `${isEtranger ? 'Étranger' : 'Marocain'} - ${s.label}`,
                              sublabel: s.code,
                            };
                          })}
                          placeholder="Sélectionner ou taper pour filtrer..."
                          disabled={isLocked}
                          error={!!errors.serieBaccalaureat}
                        />
                      )}
                    />
                    {errors.serieBaccalaureat && <p className="text-xs text-red-500 mt-1">{errors.serieBaccalaureat.message}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Année d'obtention du bac <span className="text-red-500">*</span></label>
                    <select {...register('anneeObtentionBac')} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" disabled={isLocked}>
                      <option value="">Sélectionner...</option>
                      {anneesUniversitaires.map(a => <option key={a} value={a}>{a}</option>)}
                    </select>
                    {errors.anneeObtentionBac && <p className="text-xs text-red-500 mt-1">{errors.anneeObtentionBac.message}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Diplôme d'accès <span className="text-red-500">*</span></label>
                    <Controller
                      name="diplomeAcces"
                      control={control}
                      render={({ field }) => (
                        <FilterableDropdown
                          value={field.value}
                          onChange={field.onChange}
                          options={diplomesList.map(d => ({
                            value: d.label,
                            label: d.label,
                          }))}
                          placeholder="Sélectionner ou taper pour filtrer..."
                          disabled={isLocked}
                          error={!!errors.diplomeAcces}
                        />
                      )}
                    />
                    {errors.diplomeAcces && <p className="text-xs text-red-500 mt-1">{errors.diplomeAcces.message}</p>}
                  </div>
                  {!isBacDiplome && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Spécialité du diplôme d'accès</label>
                      <input {...register('specialiteDiplomeAcces')} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" placeholder="Ex: Gestion, Informatique..." disabled={isLocked} />
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Mention du diplôme d'accès <span className="text-red-500">*</span></label>
                    <select {...register('mentionDiplomeAcces')} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" disabled={isLocked}>
                      {Object.entries(MentionDiplomeLabels).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Lieu d'obtention <span className="text-red-500">*</span></label>
                    <Controller
                      name="lieuObtentionDiplomeAcces"
                      control={control}
                      render={({ field }) => (
                        <FilterableDropdown
                          value={field.value}
                          onChange={field.onChange}
                          options={provincesList.map(p => ({
                            value: p,
                            label: p,
                          }))}
                          placeholder="Sélectionner ou taper pour filtrer..."
                          disabled={isLocked}
                          error={!!errors.lieuObtentionDiplomeAcces}
                        />
                      )}
                    />
                    {errors.lieuObtentionDiplomeAcces && <p className="text-xs text-red-500 mt-1">{errors.lieuObtentionDiplomeAcces.message}</p>}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Section 6: Statut & Aides */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <SectionHeader title="Section 6: Statut & Aides" icon={<Briefcase className="w-5 h-5 text-primary-600" />} expanded={expandedSections.status} onToggle={() => toggleSection('status')} />
            {expandedSections.status && (
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Résident (hébergement universitaire) <span className="text-red-500">*</span></label>
                    <select {...register('resident')} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" disabled={isLocked}>
                      {Object.entries(ResidentStatusLabels).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Fonctionnaire <span className="text-red-500">*</span></label>
                    <select {...register('fonctionnaire')} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" disabled={isLocked}>
                      {Object.entries(FonctionnaireStatusLabels).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Pourcentage de la bourse</label>
                    <div className="relative">
                      {/* Read-only: computed automatically from approved remises/bourses
                          against the original tuition+registration price (backend sync). */}
                      <Controller name="pourcentageBourse" control={control} render={({ field }) => (
                        <input value={field.value ?? 0} type="number" readOnly className="w-full px-4 py-2 pr-10 border border-gray-200 bg-gray-50 text-gray-600 rounded-lg cursor-not-allowed" disabled />
                      )} />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">%</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Calculé automatiquement à partir des remises et bourses accordées par l'administration</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Mobilité (échange) <span className="text-red-500">*</span></label>
                    <select {...register('mobilite')} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" disabled={isLocked}>
                      {Object.entries(MobiliteStatusLabels).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Section 7: Documents */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <SectionHeader title="Section 7: Documents" icon={<FileText className="w-5 h-5 text-primary-600" />} expanded={expandedSections.documents} onToggle={() => toggleSection('documents')} />
            {expandedSections.documents && (
              <div className="p-6 space-y-4">
                {/* Loading indicator */}
                {documentsLoading && (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-6 h-6 animate-spin text-primary-600 mr-2" />
                    <span className="text-sm text-gray-600">Chargement des documents...</span>
                  </div>
                )}

                {/* Info about automatic saving */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex gap-2">
                  <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-blue-700">
                    Les documents sont automatiquement enregistrés sur le serveur dès leur téléchargement. Vos fichiers sont sauvegardés de manière permanente.
                  </p>
                </div>

                {/* ID Document Type Selector */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Type de document d'identité</label>
                  <div className="grid grid-cols-2 gap-3">
                    <button type="button" onClick={() => setIdDocumentType('CIN')} className={`flex items-center justify-center gap-2 p-3 rounded-lg border-2 transition-all ${idDocumentType === 'CIN' ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'}`}>
                      <CreditCard className="w-5 h-5" />
                      <span className="font-medium">CIN Marocaine</span>
                    </button>
                    <button type="button" onClick={() => setIdDocumentType('PASSPORT')} className={`flex items-center justify-center gap-2 p-3 rounded-lg border-2 transition-all ${idDocumentType === 'PASSPORT' ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'}`}>
                      <BookOpen className="w-5 h-5" />
                      <span className="font-medium">Passeport</span>
                    </button>
                  </div>
                </div>

                {/* Document List */}
                <div className="bg-gray-50 rounded-lg px-4">
                  {idDocumentType === 'CIN' ? (
                    <>
                      <DocumentRow key="CIN_FRONT" label="Scan CIN - Recto" required document={getDocument('CIN_FRONT')} onUpload={f => handleUpload('CIN_FRONT', f)} onRemove={() => handleRemove('CIN_FRONT')} onPreview={() => setPreviewDocument(getDocument('CIN_FRONT') || null)} disabled={isLocked} />
                      <DocumentRow key="CIN_BACK" label="Scan CIN - Verso" required document={getDocument('CIN_BACK')} onUpload={f => handleUpload('CIN_BACK', f)} onRemove={() => handleRemove('CIN_BACK')} onPreview={() => setPreviewDocument(getDocument('CIN_BACK') || null)} disabled={isLocked} />
                    </>
                  ) : (
                    <DocumentRow key="PASSPORT" label="Scan Passeport" required document={getDocument('PASSPORT')} onUpload={f => handleUpload('PASSPORT', f)} onRemove={() => handleRemove('PASSPORT')} onPreview={() => setPreviewDocument(getDocument('PASSPORT') || null)} disabled={isLocked} />
                  )}
                  <DocumentRow label="Diplôme du Baccalauréat (scan)" required document={getDocument('DIPLOME_BAC_SCAN')} onUpload={f => handleUpload('DIPLOME_BAC_SCAN', f)} onRemove={() => handleRemove('DIPLOME_BAC_SCAN')} onPreview={() => setPreviewDocument(getDocument('DIPLOME_BAC_SCAN') || null)} disabled={isLocked} />
                  <DocumentRow label="Relevé de notes du BAC" required document={getDocument('RELEVES_NOTES_BAC')} onUpload={f => handleUpload('RELEVES_NOTES_BAC', f)} onRemove={() => handleRemove('RELEVES_NOTES_BAC')} onPreview={() => setPreviewDocument(getDocument('RELEVES_NOTES_BAC') || null)} disabled={isLocked} />
                  {requiresDiplomeAccesScan && (
                    <>
                      <DocumentRow label={`Diplôme d'accès: ${watchedDiplomeAcces}`} required document={getDocument('DIPLOME_ACCES_SCAN')} onUpload={f => handleUpload('DIPLOME_ACCES_SCAN', f)} onRemove={() => handleRemove('DIPLOME_ACCES_SCAN')} onPreview={() => setPreviewDocument(getDocument('DIPLOME_ACCES_SCAN') || null)} disabled={isLocked} />
                      <DocumentRow label={`Relevé de notes du ${watchedDiplomeAcces}`} required document={getDocument('RELEVES_NOTES_ACCES')} onUpload={f => handleUpload('RELEVES_NOTES_ACCES', f)} onRemove={() => handleRemove('RELEVES_NOTES_ACCES')} onPreview={() => setPreviewDocument(getDocument('RELEVES_NOTES_ACCES') || null)} disabled={isLocked} />
                    </>
                  )}
                  <DocumentRow label="CV" required={false} document={getDocument('CV')} onUpload={f => handleUpload('CV', f)} onRemove={() => handleRemove('CV')} onPreview={() => setPreviewDocument(getDocument('CV') || null)} disabled={isLocked} />
                  <DocumentRow label="Lettre de motivation" required={false} document={getDocument('LETTRE_MOTIVATION')} onUpload={f => handleUpload('LETTRE_MOTIVATION', f)} onRemove={() => handleRemove('LETTRE_MOTIVATION')} onPreview={() => setPreviewDocument(getDocument('LETTRE_MOTIVATION') || null)} disabled={isLocked} />
                </div>
              </div>
            )}
          </div>
        </form>

        {/* Preview Modal - Supports both images and PDFs */}
        {previewDocument && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" onClick={() => setPreviewDocument(null)}>
            <div
              className={`relative bg-white rounded-lg overflow-hidden ${
                previewDocument.previewUrl.toLowerCase().includes('.pdf')
                  ? 'w-[90vw] h-[90vh]'
                  : 'max-w-4xl max-h-[90vh]'
              }`}
              onClick={e => e.stopPropagation()}
            >
              <button onClick={() => setPreviewDocument(null)} className="absolute top-2 right-2 p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 z-10">
                <X className="w-5 h-5" />
              </button>
              {previewDocument.previewUrl.toLowerCase().includes('.pdf') ? (
                <iframe
                  src={previewDocument.previewUrl}
                  className="w-full h-full"
                  title="Document PDF"
                />
              ) : (
                <img src={previewDocument.previewUrl} alt="Document preview" className="max-w-full max-h-[90vh] object-contain" />
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
);

StudentProfileForm.displayName = 'StudentProfileForm';

export default StudentProfileForm;
